import React, { useContext } from 'react';
import {
  Button,
  Image,
  Text,
  Title3,
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useAcceptInvitationStyles } from '../styles';
import HeaderLogo from '../../../media/microsoft-advertising-logo-idp-header.png';

export type ActivationAlreadyHasAccessProps = {
  // "Use a different email" — sign the current session out and return to the invitation pre-auth
  // client page (same behavior as the "Use a different email" action on the other error pages).
  // Resolved by the caller.
  onUseDifferentEmail: () => void;
  // "Go to account" — send the user straight into Microsoft Advertising for the account they already
  // have access to (same behavior as the confirmation page's "Go to Microsoft Advertising" action).
  // Resolved by the caller.
  onGoToAccount: () => void;
};

// Shown when ActivationOptions reports `ActivationDenied` with reason
// `PuidMatch_MatchesUserUnderSameCustomer` — the signed-in identity already matches an existing user
// under the SAME customer, i.e. this email ALREADY has access to the account, so the invitation is
// redundant. This is distinct from the generic `<ActivationDeniedDialog>` ("this account can't be
// used"): here nothing is wrong — the user simply already has access — so we surface a dedicated
// page with two paths forward: retry with a different email, or go straight into the account they
// already have. Detected BEFORE the general ActivationDenied branch in the orchestrator. The
// `alreadyhasaccess` debug scenario forces the page for preview.
export const ActivationAlreadyHasAccess: React.FC<ActivationAlreadyHasAccessProps> = ({
  onUseDifferentEmail,
  onGoToAccount,
}) => {
  const i18n = useContext(I18nContext) as any;
  const classes = useAcceptInvitationStyles();

  return (
    <div className={classes.centeredPage}>
      <div
        className={classes.errorCard}
        role="region"
        aria-labelledby="activation-already-has-access-title"
        aria-describedby="activation-already-has-access-description"
      >
        <Image className={classes.brandLogo} src={HeaderLogo} alt={i18n.getString(_TL_('Microsoft Advertising'))} />

        <Title3 id="activation-already-has-access-title" className={classes.errorTitle}>
          {i18n.getString(_TL_('This email already has access'))}
        </Title3>

        <div id="activation-already-has-access-description" className={classes.errorBody}>
          <Text block>
            {i18n.getString(_TL_('The email address you used is already associated with this Microsoft Advertising account.'))}
          </Text>
          <Text block>
            {i18n.getString(_TL_('If you intended to accept this invitation with another account, use a different email address. If you want to access the account with this email, go to the account.'))}
          </Text>
        </div>

        <div className={classes.errorActions}>
          <Button
            className="already-has-access-use-different-email"
            appearance="primary"
            onClick={onUseDifferentEmail}
          >
            {i18n.getString(_TL_('Use a different email'))}
          </Button>
          <Button
            className="already-has-access-go-to-account"
            appearance="secondary"
            onClick={onGoToAccount}
          >
            {i18n.getString(_TL_('Go to account'))}
          </Button>
        </div>
      </div>
    </div>
  );
};
